import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "223",
  "slug": "university-geography-courses-games-simulations",
  "title": "Using Games and Simulations in a University Geography Seminar",
  "excerpt": "Plan a seminar around an image interpretation task or a simulation, then assess the assumptions and evidence students use.",
  "tldr": "Choose a game or simulation that fits a specific seminar objective. Give students a written task, allow time to examine the limits of the activity, and assess their explanation separately from the game result.",
  "date": "2026-03-15",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=800&q=80",
  "readTime": "3 min",
  "content": [
    {
      "type": "paragraph",
      "text": "For a remote sensing seminar, give students an unlabelled image and ask them to identify land-cover features before proposing a location. For a planning seminar, ask them to defend a land-use decision under a fixed set of constraints. Both can take the form of a game, but they require different preparation and assessment."
    },
    {
      "type": "paragraph",
      "text": "These are suggested seminar activities. They are not case studies of universities using EarthGuessr."
    },
    {
      "type": "heading",
      "text": "An image interpretation exercise"
    },
    {
      "type": "paragraph",
      "text": "Ask students to annotate the visible evidence for a location guess. Require a second plausible location and a note about what would distinguish the two. A forested landscape may be easy to describe but difficult to place without wider geographic context."
    },
    {
      "type": "paragraph",
      "text": "After revealing the answer, have students revise the annotation. Separate errors in identifying features from errors in locating them. A student might correctly identify irrigated agriculture while assuming, incorrectly, that the field pattern belongs to only one country."
    },
    {
      "type": "paragraph",
      "text": "EarthGuessr can supply a guessing round. Use an instructor-selected image instead if you need a particular resolution, spectral band combination, or study area."
    },
    {
      "type": "heading",
      "text": "A planning or negotiation simulation"
    },
    {
      "type": "paragraph",
      "text": "Give groups competing priorities and a shared map. For example, ask them to propose housing sites while preserving access to farmland and public transport. Specify the information available and the constraints each decision must meet."
    },
    {
      "type": "paragraph",
      "text": "Ask each group to record what it chose to prioritise and what it could not resolve. During the debrief, distinguish limits imposed by your exercise from constraints that would need real-world evidence."
    },
    {
      "type": "heading",
      "text": "Examine the model behind the game"
    },
    {
      "type": "paragraph",
      "text": "A simulation includes assumptions about which actions are possible and how outcomes are calculated. Ask students to identify one assumption that shaped their decision. Discuss what happens outside the simulation, including people or processes that its rules leave out."
    },
    {
      "type": "paragraph",
      "text": "For a location game, examine the image selection and scoring. A nearby guess can earn a good result without a good explanation. Conversely, sound feature identification may still leave several regions plausible."
    },
    {
      "type": "heading",
      "text": "Grade the written reasoning"
    },
    {
      "type": "paragraph",
      "text": "Collect an annotated image or a short decision memo. Assess the use of evidence, consideration of alternatives, and acknowledgement of missing information. Keep speed and competitive rank out of the rubric unless they are part of the stated learning objective."
    },
    {
      "type": "paragraph",
      "text": "Test the activity before the seminar and have the images or maps ready as a fallback. Reserve enough time for the debrief to become part of the assignment, rather than a conversation squeezed into the last minute."
    }
  ]
};
